import { Fragment, useState, useContext, useEffect, useRef } from 'react';
import { FlowForm, useFlowFormGlobal, FlowFormLeaveContext, ButtonBar, getKeyHandler } from 't-lib-ui/structure';
import { TextButton, Input, DropDownSelector, DropDownSelectorValue, Spinner } from 't-lib-ui/widgets';
import { RouterContext, goTo, useParams } from 't-lib-ui/router';
import { sidebarLeaveHandler } from '../components/LeftSidebarFocus';
import MainLayout from '../components/MainLayout';
import PageHeader from '../components/PageHeader';
import HeaderAuthButtons from '../components/HeaderAuthButtons';
import { AppState } from '../context/general';
import { FeedFilter, FeedFilterField, FeedFilterKeyword, FeedFilterOp } from '../schema-copy/api-schema';
import { normalizeFilter } from '../schema-copy/feed-filter';
import { getPlanLimits } from '../schema-copy/userPlans';
import { usePreferences } from '../connector/preferences';
import './FeedFilterPage.css';

const MAX_KEYWORDS = 10;

const OP_CHOICES: DropDownSelectorValue[] = ([
  ['contains', 'contains'],
  ['notContains', "doesn't contain"],
  ['startsWith', 'starts with'],
  ['notStartsWith', "doesn't start with"],
  ['endsWith', 'ends with'],
  ['notEndsWith', "doesn't end with"],
] as [FeedFilterOp, string][]).map(([id, text]) => ({ id, text, searchTextLc: text.toLowerCase() }));

const FIELD_CHOICES: DropDownSelectorValue[] = ([
  ['all', 'any field'],
  ['title', 'title'],
  ['body', 'text'],
  ['author', 'author'],
  ['url', 'link'],
] as [FeedFilterField, string][]).map(([id, text]) => ({ id, text, searchTextLc: text.toLowerCase() }));

const MATCH_CHOICES: DropDownSelectorValue[] = [
  { id: 'any', text: 'any of these is true', searchTextLc: 'any' },
  { id: 'all', text: 'all of these are true', searchTextLc: 'all' },
];

const emptyKeyword = (): FeedFilterKeyword => ({ text: '', field: 'all', op: 'contains' });

/** Preview line under the keyword editor; wouldMark comes from FeedEngine.previewFeedFilter.
 *  While the preview is still being calculated (wouldMark undefined) the caller shows a
 *  spinner instead. */
export function filterPreviewText(active: boolean, wouldMark: number): string {
  if (!active) return 'No conditions set - all posts stay unread until you read them.';
  if (!wouldMark) return 'None of the currently unread posts would be marked as read.';
  return `Marks ${wouldMark} currently unread ${wouldMark == 1 ? 'post' : 'posts'} as read.`;
}

const FeedFilterPage: React.FC = () => {
  useFlowFormGlobal();
  const { id: feedId } = useParams<{ id: string }>();
  const appState = useContext(AppState);
  const routerCtx = useContext(RouterContext);
  const feedEngine = appState.feedEngine;
  const [preferences, updatePreferences] = usePreferences();
  const canFilterPosts = getPlanLimits(appState.state.currentUser?.userPlan ?? 'free').keywordFilters;

  const feed = feedEngine.getFeed(feedId);
  const feedName = feed?.shortTitle || feed?.title || '';

  const [match, setMatch] = useState<'any' | 'all'>('any');
  const [keywords, setKeywords] = useState<FeedFilterKeyword[]>([emptyKeyword()]);
  const [preview, setPreview] = useState<number | undefined>(0);
  const [applying, setApplying] = useState(false);
  // Guards against out-of-order preview responses (each edit bumps the sequence).
  const previewSeq = useRef(0);

  // Initialize from the feed's current filter. The engine may still be loading on a direct
  // page load, so re-run when the feed list arrives.
  useEffect(() => {
    const init = () => {
      const filter = feedEngine.getFeedFilter(feedId);
      setMatch(filter?.match ?? 'any');
      setKeywords(filter?.keywords.length ? filter.keywords.map(k => ({ op: 'contains' as FeedFilterOp, ...k })) : [emptyKeyword()]);
    };
    init();
    return feedEngine.onFeedsChange(init);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedId, feedEngine]);

  // Debounced live preview: how many currently unread posts the edited filter would mark read.
  useEffect(() => {
    const normalized = normalizeFilter(match, keywords);
    const seq = ++previewSeq.current;
    if (!normalized) {
      setPreview(0);
      return;
    }
    setPreview(undefined);
    const timer = setTimeout(() => {
      feedEngine.previewFeedFilter(feedId, normalized)
        .then(n => { if (previewSeq.current == seq) setPreview(n); })
        .catch(() => { if (previewSeq.current == seq) setPreview(0); });
    }, 400);
    return () => clearTimeout(timer);
  }, [feedId, feedEngine, match, keywords]);

  const setKeyword = (index: number, changes: Partial<FeedFilterKeyword>) =>
    setKeywords(list => list.map((k, i) => i == index ? { ...k, ...changes } : k));

  const removeKeyword = (index: number) =>
    setKeywords(list => list.length > 1 ? list.filter((_, i) => i != index) : [emptyKeyword()]);

  const closePage = () => goTo(routerCtx, `/a/feed/${feedId}`);

  const handleApply = async () => {
    const filter = normalizeFilter(match, keywords);
    // Kick off the engine-side apply (incl. the retroactive pass over the unread backlog).
    const applied = feedEngine.setFeedFilter(feedId, filter)
      .catch(e => console.error('Failed to apply feed filter:', e));
    const next: Record<string, FeedFilter> = { ...preferences.feedFilters };
    if (filter) next[feedId] = filter;
    else delete next[feedId];
    updatePreferences({ feedFilters: next });
    // Only navigate back once the retro pass has marked the matches read: the feed page
    // snapshots the unread state when it mounts, so returning earlier would still show the
    // filtered posts (and skip the "all caught up" screen when nothing unread remains).
    // Usually instant — the pass reuses the posts the preview already fetched.
    setApplying(true);
    await applied;
    closePage();
  };

  const active = !!normalizeFilter(match, keywords);

  return (
    <FlowFormLeaveContext.Provider value={sidebarLeaveHandler}>
      <MainLayout
        selectedFeed={feedId}
        right={({ onExpand }) => (
          <div className="feedFilterPage">
            <PageHeader title="Mark as read by keyword" onMenu={onExpand} onBack={closePage} rightContent={<HeaderAuthButtons />} />
            <FlowForm main className="feedFilterContent">
              {!canFilterPosts ? (
                <p className="feedFilterNote">Marking posts as read by keyword is available on the Pro and VIP plans.</p>
              ) : (
                <>
                  {feedName && <h2 className="feedFilterFeedName">{feedName}</h2>}
                  <p className="feedFilterNote">
                    Posts that meet these conditions are marked as read automatically, so you won't see
                    them unless you show all posts. This applies to the posts already in the feed and to
                    new ones as they arrive.
                  </p>
                  <div className="feedFilterCard">
                    <div className="feedFilterMatchLine">
                      <span className="feedFilterMatchLabel">Mark as read if</span>
                      <DropDownSelector
                        selections={MATCH_CHOICES}
                        value={match}
                        focussable="flow"
                        onSelect={v => setMatch(v.id as 'any' | 'all')}
                      />
                    </div>
                    {keywords.map((kw, i) => (
                      <Fragment key={i}>
                        {i > 0 && <div className="feedFilterJoin" aria-hidden="true">{match == 'any' ? 'or' : 'and'}</div>}
                        {/* FlowForm: the field dropdown anchors the row (up/down), the rest is reached
                            with ArrowRight within the row (sideway) — like TabButtons. */}
                        <div className="feedFilterKeywordRow">
                          <DropDownSelector
                            className="feedFilterField"
                            selections={FIELD_CHOICES}
                            value={kw.field}
                            focussable="flow"
                            sidewaysBehaviour="both"
                            onSelect={v => setKeyword(i, { field: v.id as FeedFilterField })}
                          />
                          <DropDownSelector
                            className="feedFilterOp"
                            selections={OP_CHOICES}
                            value={kw.op ?? 'contains'}
                            focussable="sideway"
                            sidewaysBehaviour="both"
                            onSelect={v => setKeyword(i, { op: v.id as FeedFilterOp })}
                          />
                          <Input
                            className="feedFilterKeywordText sidewayFocus"
                            value={kw.text}
                            placeholder="word or phrase"
                            maxLength={80}
                            sidewaysBehaviour="bothText"
                            onInput={text => setKeyword(i, { text })}
                          />
                          <button
                            className="feedFilterRemove flowFocussable sidewayFocus"
                            tabIndex={0}
                            title="Remove condition"
                            onClick={() => removeKeyword(i)}
                            onKeyDown={getKeyHandler(() => removeKeyword(i))}
                          >
                            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
                              <path d="M3.5 3.5l9 9M12.5 3.5l-9 9" />
                            </svg>
                          </button>
                        </div>
                      </Fragment>
                    ))}
                    {keywords.length < MAX_KEYWORDS && (
                      <button
                        className="feedFilterAdd flowFocussable"
                        tabIndex={0}
                        onClick={() => setKeywords(list => [...list, emptyKeyword()])}
                        onKeyDown={getKeyHandler(() => setKeywords(list => [...list, emptyKeyword()]))}
                      >
                        <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                          <path d="M8 2v12M2 8h12" />
                        </svg>
                        Add condition
                      </button>
                    )}
                    <div className="feedFilterFooter">
                      <p className="feedFilterPreview">
                        {preview == undefined
                          ? <Spinner delayed className="feedFilterPreviewSpinner" />
                          : filterPreviewText(active, preview)}
                      </p>
                      <ButtonBar align="right">
                        <TextButton type="minor" onClick={closePage} label="Cancel" />
                        <TextButton type="major" focussable="sideway" busy={applying} onClick={handleApply} label="Apply" />
                      </ButtonBar>
                    </div>
                  </div>
                </>
              )}
            </FlowForm>
          </div>
        )}
      />
    </FlowFormLeaveContext.Provider>
  );
};

export default FeedFilterPage;
